/**
 * `/callback` — where the SSO round trip lands.
 *
 * Two obligations, both from SSO_INTEGRATION §3:
 *
 * 1. Exchange the code (the shell's `completeSsoCallback` does the PKCE half).
 * 2. **Restore the deep link.** Someone who opened `/clients` cold must sign in
 *    and arrive at `/clients`, not on the home page. `takeReturnTo` reads the
 *    path `SsoRedirect` stashed before it left.
 *
 * **Wave 15F** (C14): the failure is the shared `StatusPanel` with the kit's
 * button; the wait is one quiet line with a spinner, as before.
 */

import { completeSsoCallback, takeReturnTo } from '@fiveox/shell'
import { Button } from '@fiveox/ui'
import { LoaderCircle, LogIn } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import { RETURN_KEY, ssoConfig } from '../lib/sso-config'
import { session } from '../lib/session'
import { StatusPanel } from './NotFound'

export function Callback() {
  const navigate = useNavigate()
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    completeSsoCallback(ssoConfig())
      .then((tokens) => {
        if (cancelled) return
        session.storeTokens(tokens)
        // `replace`, not `push`: the callback URL carries a spent code and a
        // back button must not land on it.
        // `takeReturnTo` gives null when nothing was stashed — a direct
        // sign-in rather than a deep link. That lands on the app root.
        navigate(takeReturnTo(RETURN_KEY) ?? '/', { replace: true })
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
    // Runs once. A re-render must not attempt a second exchange with a code
    // the first one already spent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (failed) {
    return (
      <StatusPanel
        fullScreen
        icon={<LogIn size={22} aria-hidden />}
        title="Sign-in could not be completed"
        description="The sign-in page handed you back, but ProDesk couldn’t finish signing you in. Trying again usually works."
        actions={<Button onClick={() => navigate('/', { replace: true })}>Try again</Button>}
      />
    )
  }

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-6">
      <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
        <LoaderCircle size={16} className="animate-spin" aria-hidden /> Signing you in…
      </p>
    </div>
  )
}
